import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Plus, Trash2 } from 'lucide-react';
import { usersAPI } from '../../services/api';

const emptyStep = () => ({
  id: `${Date.now()}-${Math.random()}`,
  userId: '',
  name: '',
  email: '',
  role: 'reviewer',
  dueDate: '',
  isFinalApprover: false,
});

const buildDefaultSteps = (users = []) => {
  if (!users.length) return [emptyStep()];
  return users.map((user) => ({
    id: `${user._id || user.id}-${Date.now()}`,
    userId: user._id || user.id || '',
    name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
    email: user.email || '',
    role: user.role || 'reviewer',
    dueDate: '',
    isFinalApprover: false,
  }));
};

export default function ClientWorkflowSetupModal({
  orgId,
  initialWorkflow,
  onSave,
  onClose,
}) {
  const { data: usersData = [] } = useQuery({
    queryKey: ['clientWorkflowUsers', orgId],
    queryFn: () => usersAPI.getAll(),
    select: (res) => res.data?.users || res.data || [],
    enabled: !!orgId,
  });

  const clientUsers = useMemo(() => {
    return (usersData || []).filter((person) => {
      const userOrgId = person.organizationId?._id || person.organizationId;
      return orgId && userOrgId?.toString() === orgId.toString();
    });
  }, [usersData, orgId]);

  const initialClient = useMemo(() => {
    if (initialWorkflow?.client?.steps?.length) {
      return initialWorkflow.client.steps.map((step) => ({
        id: `${step._id || Date.now()}-${Math.random()}`,
        userId: step.userId || '',
        name: step.name || '',
        email: step.email || '',
        role: step.role || 'reviewer',
        dueDate: step.dueDate ? step.dueDate.slice(0, 10) : '',
        isFinalApprover: !!step.isFinalApprover,
      }));
    }
    return buildDefaultSteps(clientUsers);
  }, [clientUsers, initialWorkflow]);

  const [clientSteps, setClientSteps] = useState(initialClient);
  const [errors, setErrors] = useState({});

  const updateStep = (stepId, field, value) => {
    setClientSteps((steps) =>
      steps.map((step) => (step.id === stepId ? { ...step, [field]: value } : step))
    );
  };

  const addStep = () => {
    setClientSteps((steps) => [...steps, emptyStep()]);
  };

  const removeStep = (stepId) => {
    setClientSteps((steps) => steps.filter((step) => step.id !== stepId));
  };

  const validate = () => {
    const nextErrors = {};
    if (!clientSteps.length) {
      nextErrors.client = 'Add at least one client reviewer.';
    }
    if (!clientSteps.some((step) => step.role === 'lead')) {
      nextErrors.clientLead = 'Select a client lead reviewer.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const existingInternalSteps = initialWorkflow?.internal?.steps || [];
    const payload = {
      internal: {
        steps: existingInternalSteps.map((step, index) => ({
          userId: step.userId || undefined,
          name: step.name,
          email: step.email,
          role: step.role || 'reviewer',
          dueDate: step.dueDate || null,
          isFinalApprover: !!step.isFinalApprover,
          status: step.status || (index === 0 ? 'active' : 'pending'),
        })),
      },
      client: {
        steps: clientSteps.map((step, index) => ({
          userId: step.userId || undefined,
          name: step.name,
          email: step.email,
          role: step.role,
          dueDate: step.dueDate || null,
          isFinalApprover: step.isFinalApprover,
          status: index === 0 ? 'active' : 'pending',
        })),
      },
      activeTeam: initialWorkflow?.activeTeam || 'internal',
    };
    onSave?.(payload);
  };

  const renderStep = (step, users) => (
    <div key={step.id} className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_auto] gap-3 items-end">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Reviewer</label>
        <select
          value={step.userId}
          onChange={(e) => updateStep(step.id, 'userId', e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">Select reviewer</option>
          {users.map((user, index) => (
            <option key={`${user._id || user.id || 'user'}-${index}`} value={user._id || user.id}>
              {user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim()}
            </option>
          ))}
        </select>
        {!users.length && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="Name"
              value={step.name}
              onChange={(e) => updateStep(step.id, 'name', e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <input
              type="email"
              placeholder="Email"
              value={step.email}
              onChange={(e) => updateStep(step.id, 'email', e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        )}
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
        <select
          value={step.role}
          onChange={(e) => updateStep(step.id, 'role', e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="lead">Lead</option>
          <option value="reviewer">Reviewer</option>
          <option value="approver">Approver</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Due date</label>
        <input
          type="date"
          value={step.dueDate}
          onChange={(e) => updateStep(step.id, 'dueDate', e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-600 flex items-center gap-2">
          <input
            type="checkbox"
            checked={step.isFinalApprover}
            onChange={(e) => updateStep(step.id, 'isFinalApprover', e.target.checked)}
            className="h-4 w-4"
          />
          Final approver
        </label>
        <button
          type="button"
          onClick={() => removeStep(step.id)}
          className="text-gray-400 hover:text-red-500"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl mx-4 max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Set Up Collaboration Workflow</h2>
          <p className="text-sm text-gray-600 mt-1">
            Define the review order, due dates, and final approver for internal and client teams.
          </p>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {(errors.client || errors.clientLead) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
              <AlertCircle className="text-red-600 mt-0.5" size={18} />
              <div className="text-sm text-red-700 space-y-1">
                {errors.client && <div>{errors.client}</div>}
                {errors.clientLead && <div>{errors.clientLead}</div>}
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Client review order</h3>
              <button
                type="button"
                onClick={addStep}
                className="inline-flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700"
              >
                <Plus size={16} />
                Add reviewer
              </button>
            </div>
            <div className="space-y-4">
              {clientSteps.map((step) => renderStep(step, clientUsers))}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
          >
            Save Workflow
          </button>
        </div>
      </div>
    </div>
  );
}
